<script>
// Menu de usuário do topbar. Header com avatar (clicável para trocar a foto) +
// nome + escritório, e as mesmas ações da nav antiga (nav-bar.vue): Meus dados,
// Chaves de API, Extensão AAWZ, Enviar sugestão/dúvida/problema, Alterar senha e
// Sair. As ações disparam via GlobalBus (ou $router); os modais correspondentes
// vivem no host único <GlobalModals> do main.vue, sempre montado.
import ProfessionalAvatar from '@modules/partnership/components/professional-avatar.vue'
import { GlobalBus } from '@services/bus'
import con from '@connection'
import { user } from '@constants/URL'
import { MODAL, FEATURES } from '@constants/Text'
import { podeConectarWhatsapp } from '@modules/aawz-intelligence-v2/utils/whatsapp-ia-enabled'

const ITEM_ICON_SIZE = 16

export default {
  name: 'TopbarUserMenu',
  components: { ProfessionalAvatar },
  data() {
    return { ITEM_ICON_SIZE, menuOpen: false }
  },
  computed: {
    currentUser() {
      return this.$currentUser()
    },
    userName() {
      return this.currentUser.nome || 'Usuário'
    },
    officeName() {
      const office = this.currentUser.escritorio
      return (office && office.nome) || ''
    },
    // Nível de acesso (ex: "Acesso: Gestor"). Some para o cliente final.
    showAccess() {
      return !this.currentUser.IS_CLIENTE
    },
    perfilAccess() {
      const user = this.currentUser
      if (user.tipo_usuario === 'profissional') {
        return user.leader ? 'Líder' : 'Profissional'
      }
      return 'Gestor'
    },
    // Paridade com o menu de usuário da nav antiga (nav-bar.vue): estes itens
    // só aparecem para escritórios/usuários com a feature correspondente. Além
    // disso, nunca fazem sentido pro cliente final — são ferramentas do
    // profissional (gerenciar chaves de integração / parear a extensão de
    // captura de reuniões). O mesmo gap (sem check IS_CLIENTE) já existia na
    // nav antiga; corrigido aqui e lá junto.
    canShowChavesApi() {
      if (this.currentUser.IS_CLIENTE) return false
      return Boolean(this.$access[FEATURES.CHAT_IA])
    },
    canShowExtensao() {
      if (this.currentUser.IS_CLIENTE) return false
      return Boolean(this.$access[FEATURES.TRANSCRICOES])
    },
    // Canal WhatsApp da IA: espelho das 3 flags do backend (whatsapp/core/gate.py).
    canShowWhatsapp() {
      return podeConectarWhatsapp({
        user: this.currentUser,
        access: this.$access,
      })
    },
  },
  methods: {
    // O v-menu usa close-on-content-click=false (para o header "Trocar foto" não
    // fechar sozinho), então cada ação fecha o menu explicitamente — senão ele
    // fica preso aberto atrás do modal/rota de destino.
    closeMenu() {
      this.menuOpen = false
    },
    openAvatarUpload() {
      this.closeMenu()
      GlobalBus.$emit(MODAL.AVATAR_UPLOAD)
    },
    openMeusDados() {
      this.closeMenu()
      GlobalBus.$emit(MODAL.MEUS_DADOS, this.currentUser, true)
    },
    openChavesApi() {
      this.closeMenu()
      this.$router
        .push({ name: 'aawz-intelligence.chaves-api' })
        .catch(() => {})
    },
    openExtensaoAawz() {
      this.closeMenu()
      GlobalBus.$emit(MODAL.EXTENSION_TOKEN)
    },
    openConectarWhatsapp() {
      this.closeMenu()
      GlobalBus.$emit(MODAL.IA_WHATSAPP_CONECTAR)
    },
    openSuggest() {
      this.closeMenu()
      GlobalBus.$emit(MODAL.SUGGEST_IMPROVEMENT, true)
    },
    openAlterarSenha() {
      this.closeMenu()
      GlobalBus.$emit(MODAL.CHANGE_PASSWORD, true)
    },
    logout() {
      this.closeMenu()
      con.post(user.logout).then(() => {
        globalThis.location.href = user.tela_login
      })
    },
  },
}
</script>

<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    left
    min-width="260"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on }">
      <button class="topbar-user" type="button" title="Conta" v-on="on">
        <ProfessionalAvatar
          :professional="currentUser"
          :avatarUrl="currentUser.avatar_url"
          size="36px"
          textSize="12px"
        />
      </button>
    </template>

    <div class="tb-user-menu">
      <!-- Header: avatar clicável para trocar a foto + nome + escritório -->
      <button
        class="tb-user-menu__head"
        type="button"
        @click="openAvatarUpload"
      >
        <span class="tb-user-menu__avatar">
          <ProfessionalAvatar
            :professional="currentUser"
            :avatarUrl="currentUser.avatar_url"
            size="46px"
            textSize="15px"
          />
          <span class="tb-user-menu__cam">
            <icon-svg name="mdi-camera" :size="12" color="#ffffff" />
          </span>
        </span>
        <span class="tb-user-menu__info">
          <span class="tb-user-menu__name">{{ userName }}</span>
          <span v-if="officeName" class="tb-user-menu__office">
            {{ officeName }}
          </span>
          <span v-if="showAccess" class="tb-user-menu__access">
            Acesso: <strong>{{ perfilAccess }}</strong>
          </span>
          <span class="tb-user-menu__hint">Trocar foto</span>
        </span>
      </button>

      <v-divider />

      <v-list dense>
        <v-list-item @click="openMeusDados">
          <v-list-item-icon class="mr-3">
            <icon-svg name="user" :size="ITEM_ICON_SIZE" color="#5b5b5b" />
          </v-list-item-icon>
          <v-list-item-title>Meus dados</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="canShowChavesApi" @click="openChavesApi">
          <v-list-item-icon class="mr-3">
            <icon-svg
              name="mdi-key-outline"
              :size="ITEM_ICON_SIZE"
              color="#5b5b5b"
            />
          </v-list-item-icon>
          <v-list-item-title>Chaves de API</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="canShowExtensao" @click="openExtensaoAawz">
          <v-list-item-icon class="mr-3">
            <icon-svg
              name="mdi-puzzle-outline"
              :size="ITEM_ICON_SIZE"
              color="#5b5b5b"
            />
          </v-list-item-icon>
          <v-list-item-title>Extensão AAWZ</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="canShowWhatsapp" @click="openConectarWhatsapp">
          <v-list-item-icon class="mr-3">
            <icon-svg
              name="mdi-whatsapp"
              :size="ITEM_ICON_SIZE"
              color="#5b5b5b"
            />
          </v-list-item-icon>
          <v-list-item-title>Conectar WhatsApp</v-list-item-title>
        </v-list-item>
        <v-list-item @click="openSuggest">
          <v-list-item-icon class="mr-3">
            <icon-svg
              name="mdi-message-text-outline"
              :size="ITEM_ICON_SIZE"
              color="#5b5b5b"
            />
          </v-list-item-icon>
          <v-list-item-title>Enviar sugestão/dúvida/problema</v-list-item-title>
        </v-list-item>
        <v-list-item @click="openAlterarSenha">
          <v-list-item-icon class="mr-3">
            <icon-svg
              name="mdi-lock-outline"
              :size="ITEM_ICON_SIZE"
              color="#5b5b5b"
            />
          </v-list-item-icon>
          <v-list-item-title>Alterar senha</v-list-item-title>
        </v-list-item>
        <v-divider />
        <v-list-item @click="logout">
          <v-list-item-icon class="mr-3">
            <icon-svg
              name="mdi-logout"
              :size="ITEM_ICON_SIZE"
              color="#c03939"
            />
          </v-list-item-icon>
          <v-list-item-title class="tb-user-menu__logout"
            >Sair</v-list-item-title
          >
        </v-list-item>
      </v-list>
    </div>
  </v-menu>
</template>

<style lang="sass" scoped>
.topbar-user
  display: inline-flex
  align-items: center
  justify-content: center
  padding: 0
  border: 0
  border-radius: 999px
  background: transparent
  cursor: pointer
  transition: box-shadow 0.15s

  &:hover
    box-shadow: 0 0 0 3px rgba(86, 175, 121, 0.18)

.tb-user-menu
  background: #fff

  &__head
    display: flex
    align-items: center
    gap: 12px
    width: 100%
    padding: 16px
    border: 0
    background: transparent
    cursor: pointer
    text-align: left

    &:hover
      background: #f5f6fa

  &__avatar
    position: relative
    flex-shrink: 0

  &__cam
    position: absolute
    right: -2px
    bottom: -2px
    width: 18px
    height: 18px
    border-radius: 999px
    background: var(--primary)
    border: 2px solid #fff
    display: flex
    align-items: center
    justify-content: center

  &__info
    min-width: 0
    display: flex
    flex-direction: column

  &__name
    font-size: 14px
    font-weight: 700
    color: #030309
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

  &__office
    font-size: 12px
    color: #8e8e8e

  &__access
    font-size: 12px
    color: #8e8e8e
    margin-top: 2px

    strong
      font-weight: 600
      color: #030309

  &__hint
    font-size: 11px
    font-weight: 700
    color: var(--primary)
    margin-top: 2px

  &__logout
    color: #c03939
    font-weight: 600
</style>
